import m from "mithril";
import { store } from "../models/store.js";
import { scan } from "../services/scan.js";
import type { Region } from "../models/types.js";

// Shows the loaded photo and lets you drag a box over it. If the automatic scan
// of the whole receipt is poor, draw a box around just the items and rescan that
// region. Coordinates are tracked in CSS pixels for drawing, then converted to
// natural image pixels for the actual crop.

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function ImageViewer(): m.Component {
  let imgEl: HTMLImageElement | null = null;
  let drawing = false;
  let box: Box | null = null;

  function getLocalPoint(ev: PointerEvent): { x: number; y: number } {
    const rect = imgEl!.getBoundingClientRect();
    return {
      x: Math.min(Math.max(ev.clientX - rect.left, 0), rect.width),
      y: Math.min(Math.max(ev.clientY - rect.top, 0), rect.height),
    };
  }

  function commitRegion() {
    if (!box || !imgEl) return;
    const rect = imgEl.getBoundingClientRect();
    const scaleX = imgEl.naturalWidth / rect.width;
    const scaleY = imgEl.naturalHeight / rect.height;
    const left = Math.min(box.x0, box.x1);
    const top = Math.min(box.y0, box.y1);
    const width = Math.abs(box.x1 - box.x0);
    const height = Math.abs(box.y1 - box.y0);
    if (width < 8 || height < 8) {
      store.region = null; // treat a tiny drag as "clear"
      box = null;
      return;
    }
    const region: Region = {
      x: Math.round(left * scaleX),
      y: Math.round(top * scaleY),
      w: Math.round(width * scaleX),
      h: Math.round(height * scaleY),
    };
    store.region = region;
  }

  function onDown(ev: PointerEvent) {
    if (!imgEl) return;
    ev.preventDefault();
    drawing = true;
    const point = getLocalPoint(ev);
    box = { x0: point.x, y0: point.y, x1: point.x, y1: point.y };
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
  }
  function onMove(ev: PointerEvent) {
    if (!drawing || !box) return;
    const point = getLocalPoint(ev);
    box.x1 = point.x;
    box.y1 = point.y;
    m.redraw();
  }
  function onUp() {
    if (!drawing) return;
    drawing = false;
    commitRegion();
    m.redraw();
  }

  function getSelectionStyle(): string {
    if (!box) return "display:none";
    const left = Math.min(box.x0, box.x1);
    const top = Math.min(box.y0, box.y1);
    const width = Math.abs(box.x1 - box.x0);
    const height = Math.abs(box.y1 - box.y0);
    return `left:${left}px;top:${top}px;width:${width}px;height:${height}px`;
  }

  const isBusy = () => store.phase === "ocr" || store.phase === "parsing";

  return {
    view() {
      if (!store.imageUrl) return null;
      return m("div.viewer", [
        m(
          "div.image-wrap",
          {
            onpointerdown: onDown,
            onpointermove: onMove,
            onpointerup: onUp,
            onpointercancel: onUp,
          },
          [
            m("img.receipt", {
              src: store.imageUrl,
              alt: "Receipt",
              draggable: false,
              oncreate: (vnode: m.VnodeDOM) => {
                imgEl = vnode.dom as HTMLImageElement;
              },
            }),
            m("div.selection", { style: getSelectionStyle() }),
          ],
        ),
        m("div.viewer-actions", [
          m(
            "button.btn",
            { disabled: isBusy(), onclick: () => void scan(false) },
            isBusy() ? "Scanning…" : "🔁 Scan whole photo",
          ),
          m(
            "button.btn",
            {
              disabled: isBusy() || !store.region,
              onclick: () => void scan(true),
            },
            "🔍 Scan selected box",
          ),
          store.region
            ? m(
                "button.btn.btn-ghost",
                {
                  onclick: () => {
                    store.region = null;
                    box = null;
                  },
                },
                "Clear box",
              )
            : null,
        ]),
        store.region
          ? m("p.hint", "Box set — “Scan selected box” reads just that area.")
          : m("p.hint", "Tip: drag a box over the items if the scan misses them."),
      ]);
    },
  };
}
